import React from "react";
import TimesheetWorkerInput from "../../../screens/user/TimesheetWorkerInput";
import VStack from "../../../ui_atoms/VStack";
import { MockedProvider } from "@apollo/client/testing";
import {
    buildBreakdown,
    buildBreakdownFromTrackingDates,
    buildContract,
    buildMission,
    buildMissionInfo,
    buildMissionVacationStatus,
    buildTimesheet,
} from "../../factories";
import {
    GET_DURATION_BREAKDOWN,
    GET_MISSION_VACATION_STATUS,
    GET_TIME_SHEET,
    SAVE_TIME_SHEET,
    SUBMIT_EXACT_TIME_SHEET,
} from "../../../graphql/timesheet";
import { GET_MISSION } from "../../../graphql/core";
import type { Meta, StoryObj } from "@storybook/react";
import { createStackNavigator } from "@react-navigation/stack";
import {
    DateScheduleInput,
    EarningType,
    Repetition,
    TimeSheetState,
    Unit,
} from "../../../__generated__/graphql";
import { formatDateForGraphql } from "../../../utils";
import { addDays } from "date-fns";

const Stack = createStackNavigator<{
    TimesheetWorkerInput: { id: string };
}>();

const StoryComponent = ({ id }: { id: string }) => {
    const mocks = [
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-open" },
            },
            newData: () => ({
                data: {
                    getTimeSheet: buildTimesheet({ status: TimeSheetState.Open }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-open-hourly" },
            },
            newData: () => ({
                data: {
                    getTimeSheet: buildTimesheet({
                        id: "test-open-hourly",
                        status: TimeSheetState.Open,
                        fromDate: formatDateForGraphql(addDays(new Date(), -6)),
                        toDate: formatDateForGraphql(addDays(new Date(), -1)),
                        acceptedAt: null,
                        autoAcceptAt: null,
                        submittedAt: null,
                        rejectedAt: null,
                        acceptedBy: null,
                        missionInfo: buildMissionInfo({
                            id: "mission-open-hourly",
                            earningType: EarningType.Hourly,
                        }),
                    }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-submitted" },
            },
            newData: () => ({
                data: {
                    getTimeSheet: buildTimesheet({ status: TimeSheetState.Submitted }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-invalid" },
            },
            newData: () => ({
                data: {
                    getTimeSheet: buildTimesheet({
                        status: TimeSheetState.Open,
                        fromDate: formatDateForGraphql(addDays(new Date(), -1)),
                        toDate: formatDateForGraphql(addDays(new Date(), 0)),
                        trackingDates: [],
                    }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-45hours" },
            },
            newData: () => ({
                data: {
                    getTimeSheet: buildTimesheet({
                        status: TimeSheetState.Open,
                        fromDate: formatDateForGraphql(addDays(new Date(), 0)),
                        toDate: formatDateForGraphql(addDays(new Date(), 2)),
                        missionInfo: buildMissionInfo({ id: "mission-45hours" }),
                        trackingDates: [],
                    }),
                },
            }),
        },
        {
            variableMatcher: () => true,

            request: {
                query: GET_MISSION,
            },
            newData: ({ id }: { id: string }) => {
                if (id === "mission-45hours") {
                    return {
                        data: {
                            getMission: buildMission({
                                currentContract: buildContract({
                                    startDate: formatDateForGraphql(addDays(new Date(), -7)),
                                    endDate: null,
                                    hoursPerWeekFullTime: 45,
                                    workingTimeClassicSpec: {
                                        repetition: Repetition.Week,
                                        unit: Unit.Hours,
                                        unitCount: 45,
                                    },
                                }),
                            }),
                        },
                    };
                }
                return {
                    data: {
                        getMission: buildMission({
                            currentContract: buildContract({
                                startDate: formatDateForGraphql(addDays(new Date(), -7)),
                                endDate: null,
                            }),
                        }),
                    },
                };
            },
        },
        {
            variableMatcher: () => true,

            request: {
                query: GET_DURATION_BREAKDOWN,
            },
            newData: ({ trackingDates }: { trackingDates: [DateScheduleInput] }) => {
                return {
                    data: {
                        getDurationBreakdown: buildBreakdown(
                            buildBreakdownFromTrackingDates(trackingDates),
                        ),
                    },
                };
            },
        },
        {
            variableMatcher: () => true,

            request: {
                query: SAVE_TIME_SHEET,
            },
            newData: ({
                trackingDates,
            }: {
                timeSheetId: string;
                trackingDates: [DateScheduleInput];
            }) => {
                // eslint-disable-next-line no-console
                console.log("Save timesheet", JSON.stringify(trackingDates, null, 2));
                return {
                    data: {
                        saveTimeSheet: buildTimesheet({}),
                    },
                };
            },
        },
        {
            variableMatcher: () => true,

            request: {
                query: SUBMIT_EXACT_TIME_SHEET,
            },
            newData: ({
                trackingDates,
            }: {
                timeSheetId: string;
                trackingDates: [DateScheduleInput];
            }) => {
                // eslint-disable-next-line no-console
                console.log("Submit timesheet", JSON.stringify(trackingDates, null, 2));
                return {
                    data: {
                        submitExactTimeSheet: { id: "timesheet-id" },
                    },
                };
            },
        },
        {
            variableMatcher: () => true,
            maxUsageCount: Number.POSITIVE_INFINITY,
            request: {
                query: GET_MISSION_VACATION_STATUS,
            },
            result: () => {
                return {
                    data: {
                        getMissionVacationStatus: buildMissionVacationStatus({
                            availableVacationMinutes: 180, // 3 hours
                            availablePendingVacationMinutes: 60, // 1 hour available not yet approved
                        }),
                    },
                };
            },
        },
    ];
    return (
        <MockedProvider mocks={mocks}>
            <VStack alignment="stretch" style={{ flex: 1 }}>
                <Stack.Navigator initialRouteName="TimesheetWorkerInput">
                    <Stack.Screen
                        name="TimesheetWorkerInput"
                        component={TimesheetWorkerInput}
                        initialParams={{ id }}
                    />
                </Stack.Navigator>
            </VStack>
        </MockedProvider>
    );
};
const meta = {
    title: "Timesheet/Worker input",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Cycle: Story = {
    args: {
        id: "test-open",
    },
};

export const OpenStatusMonthly: Story = {
    args: {
        id: "test-open",
    },
};

export const OpenStatusHourly: Story = {
    args: {
        id: "test-open-hourly",
    },
};

export const SubmittedStatus: Story = {
    args: {
        id: "test-submitted",
    },
};

export const InvalidTimesheet: Story = {
    args: {
        id: "test-invalid",
    },
};

export const Contract45Hours: Story = {
    args: {
        id: "test-45hours",
    },
};
